import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import FormInput from '../../../components/FormInput';
import { FaEdit, FaTrash, FaTimes } from 'react-icons/fa';

const Categories = () => {
  const { data: categories, loading: categoriesLoading, fetchData: fetchCategories } = useApi('/content/categories');
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    subcategories: [],
    newSubcategory: '',
  });
  const [categoryErrors, setCategoryErrors] = useState({});

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const validateCategoryForm = () => {
    const errors = {};
    if (!categoryForm.name.trim()) errors.name = 'Category name is required';
    setCategoryErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setCategoryForm({ ...categoryForm, [name]: value });
    setCategoryErrors({ ...categoryErrors, [name]: '' });
  };

  const handleSubcategoryKeyDown = (e) => {
    if (e.key === 'Enter' && categoryForm.newSubcategory.trim()) {
      e.preventDefault();
      const newSub = categoryForm.newSubcategory.trim();
      if (!categoryForm.subcategories.includes(newSub)) {
        setCategoryForm({
          ...categoryForm,
          subcategories: [...categoryForm.subcategories, newSub],
          newSubcategory: '',
        });
      } else {
        toast.error('Subcategory already exists');
      }
    }
  };

  const handleRemoveSubcategory = (subcategory) => {
    setCategoryForm({
      ...categoryForm,
      subcategories: categoryForm.subcategories.filter(sub => sub !== subcategory),
    });
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!validateCategoryForm()) {
      toast.error('Please fix form errors');
      return;
    }
    try {
      const payload = {
        name: categoryForm.name,
        subcategories: categoryForm.subcategories,
      };
      if (editingCategoryId) {
        await apiService.put(`/content/categories/${editingCategoryId}`, payload);
        toast.success('Category updated successfully');
      } else {
        await apiService.post('/content/categories', payload);
        toast.success('Category added successfully');
      }
      fetchCategories();
      setIsCategoryModalOpen(false);
      setCategoryForm({ name: '', subcategories: [], newSubcategory: '' });
      setEditingCategoryId(null);
      setCategoryErrors({});
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const handleEditCategory = (category) => {
    setCategoryForm({
      name: category.name || '',
      subcategories: category.subcategories || [],
      newSubcategory: '',
    });
    setEditingCategoryId(category._id);
    setIsCategoryModalOpen(true);
    setCategoryErrors({});
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category and its content?')) return;
    try {
      await apiService.delete(`/content/categories/${categoryId}`);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (err) {
      // Error handled by apiService interceptor
    }
  };

  const categoryColumns = [
    { key: 'name', label: 'Name' },
    { key: 'subcategories', label: 'Subcategories', render: (item) => item.subcategories.join(', ') || 'None' },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEditCategory(item)}
            className="text-yellow-500 hover:text-yellow-600"
            title="Edit"
          >
            <FaEdit />
          </button>
          <button
            onClick={() => handleDeleteCategory(item._id)}
            className="text-red-500 hover:text-red-600"
            title="Delete"
          >
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold dark:text-white">Categories</h3>
        <button
          onClick={() => setIsCategoryModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Add Category
        </button>
      </div>
      {categoriesLoading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">Loading...</div>
      ) : !categories || categories.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300">No categories found.</div>
      ) : (
        <div className="card">
          <Table columns={categoryColumns} data={categories || []} />
        </div>
      )}
      <Modal
        isOpen={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
          setCategoryForm({ name: '', subcategories: [], newSubcategory: '' });
          setEditingCategoryId(null);
          setCategoryErrors({});
        }}
        title={editingCategoryId ? 'Edit Category' : 'Add Category'}
        className="max-w-full lg:max-w-md mx-4"
      >
        <div className="p-4">
          <form onSubmit={handleCategorySubmit}>
            <div className="space-y-4">
              <FormInput
                label="Category Name"
                name="name"
                value={categoryForm.name}
                onChange={handleCategoryInputChange}
                required
                placeholder="Enter category name"
                error={categoryErrors.name}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Subcategories (press Enter to add)
                </label>
                <input
                  type="text"
                  name="newSubcategory"
                  value={categoryForm.newSubcategory}
                  onChange={handleCategoryInputChange}
                  onKeyDown={handleSubcategoryKeyDown}
                  placeholder="Type and press Enter to add subcategory"
                  className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                />
                {categoryForm.subcategories.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {categoryForm.subcategories.map((sub, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                      >
                        {sub}
                        <button
                          type="button"
                          onClick={() => handleRemoveSubcategory(sub)}
                          className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                        >
                          <FaTimes size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsCategoryModalOpen(false);
                  setCategoryForm({ name: '', subcategories: [], newSubcategory: '' });
                  setCategoryErrors({});
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={categoriesLoading}
              >
                {editingCategoryId ? 'Update Category' : 'Add Category'}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default Categories;